import { RefreshError } from './RefreshError';
import { LoadingIndicator } from './LoadingIndicator';
import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import type {
  ColDef,
  GetRowIdParams,
  RowClickedEvent,
  CellMouseDownEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useScans } from '../hooks/useScans';
import type { ScanListItem } from '../types/scans';
import { ErrorDisplay } from './ErrorDisplay';
import {
  TimeAgoCellRenderer,
  NumberCellRenderer,
} from './ag-grid/cellRenderers';
import { CopyButton } from './ag-grid/cellRenderers';
import { isCopyButtonClick } from './ag-grid/isCopyButtonClick';
import { isLinkClick } from './ag-grid/isLinkClick';
import { PageHeader } from './PageHeader';
import { ListSkeleton } from './ListSkeleton';
import { Pagination } from './Pagination';
import { useClampPage } from '../hooks/useClampPage';
import { useGridLayout } from '../hooks/useGridLayout';
import { ResetGridLayoutButton } from './ag-grid/ResetGridLayoutButton';
import './ag-grid/styles.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const PAGE_SIZE = 50;

function ErrorsCellRenderer({ value }: { value: string[] | null }) {
  if (!value || value.length === 0) return <span>-</span>;
  const errorCount = value.length;
  const preview =
    value[0].length > 50 ? value[0].slice(0, 50) + '...' : value[0];
  const tooltip = value.join('\n');
  return (
    <span
      className="block py-2 whitespace-normal text-xs leading-4 text-err"
      title={tooltip}
    >
      {errorCount > 1 ? `${errorCount} errors: ${preview}` : preview}
    </span>
  );
}

const viewHref = (scan: ScanListItem) =>
  `/scan/${encodeURIComponent(scan.scan_folder)}`;

// The scan's name as the link into it (full document load: the Scout viewer
// ships its own global stylesheet), with a copy button for the folder id.
function ScanNameCell({ data }: { data: ScanListItem }) {
  const label = data.meta_name || data.scan_name || data.scan_folder;
  return (
    <span className="copy-button-cell">
      <a
        href={viewHref(data)}
        className="copy-button-cell-text text-sm font-medium text-ink no-underline hover:text-accent hover:underline"
        title={label}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {label}
      </a>
      <CopyButton value={data.scan_folder} />
    </span>
  );
}

export function ScanList() {
  const gridRef = useRef<AgGridReact<ScanListItem>>(null);
  // Search and page live in the URL so they survive opening a scan and coming
  // back, reloads, and sharing.
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') ?? '';
  const rawPage = parseInt(searchParams.get('page') ?? '1', 10);
  const urlPage = Number.isFinite(rawPage) && rawPage >= 1 ? rawPage : 1;

  const {
    scans,
    isLoading,
    hasData,
    error,
    total,
    page,
    setPage,
    setSearch,
    refetch,
  } = useScans({
    page: urlPage,
    limit: PAGE_SIZE,
    search: searchQuery,
  });
  useEffect(() => {
    setSearch(searchQuery);
    setPage(urlPage);
  }, [searchQuery, urlPage, setSearch, setPage]);

  const updateParams = useCallback(
    (next: { search?: string; page?: number }) => {
      setSearchParams(
        prev => {
          const out = new URLSearchParams(prev);
          if (next.search !== undefined) {
            if (next.search) out.set('search', next.search);
            else out.delete('search');
          }
          if (next.page !== undefined) {
            if (next.page > 1) out.set('page', String(next.page));
            else out.delete('page');
          }
          return out;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const hasLoaded = hasData;

  const handleRowClicked = useCallback(
    (event: RowClickedEvent<ScanListItem>) => {
      const mouseEvent = event.event as MouseEvent | undefined;
      if (
        mouseEvent?.button === 1 ||
        mouseEvent?.ctrlKey ||
        mouseEvent?.metaKey
      ) {
        return;
      }
      // The name link and the copy button handle their own clicks.
      if (isLinkClick(event) || isCopyButtonClick(event)) return;
      const scan = event.data;
      if (!scan) return;
      window.location.href = viewHref(scan);
    },
    []
  );

  const handleCellMouseDown = useCallback(
    (event: CellMouseDownEvent<ScanListItem>) => {
      const mouseEvent = event.event as MouseEvent;
      if (mouseEvent.button === 1 || mouseEvent.ctrlKey || mouseEvent.metaKey) {
        // Modifier clicks on the link itself already open a new tab.
        if (isLinkClick(event)) return;
        const scan = event.data;
        if (!scan) return;
        window.open(viewHref(scan), '_blank');
      }
    },
    []
  );

  const handlePageChange = useCallback(
    (newPage: number) => updateParams({ page: newPage }),
    [updateParams]
  );

  const totalPages = Math.ceil(total / PAGE_SIZE);
  useClampPage(
    urlPage,
    totalPages,
    hasData && !isLoading && !error,
    handlePageChange
  );

  const columnDefs = useMemo<ColDef<ScanListItem>[]>(
    () => [
      {
        field: 'meta_name',
        headerName: 'Name',
        width: 350,
        minWidth: 260,
        cellRenderer: ScanNameCell,
      },
      {
        field: 'scanner_result_count',
        headerName: 'Results',
        width: 100,
        cellRenderer: NumberCellRenderer,
      },
      {
        field: 'errors',
        headerName: 'Errors',
        width: 260,
        autoHeight: true,
        cellRenderer: ErrorsCellRenderer,
      },
      {
        field: 'timestamp',
        headerName: 'Timestamp',
        width: 150,
        cellRenderer: TimeAgoCellRenderer,
      },
      {
        field: 'job_id',
        headerName: 'Job ID',
        width: 220,
        valueFormatter: params => params.value || '-',
      },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef<ScanListItem>>(
    () => ({
      sortable: true,
      resizable: true,
      filter: false,
    }),
    []
  );

  const getRowId = useCallback(
    (params: GetRowIdParams<ScanListItem>) => params.data.scan_id,
    []
  );

  const layout = useGridLayout('scans');

  if (error && !hasData) {
    return <ErrorDisplay message={error.toString()} />;
  }

  return (
    <div className="pane flex h-full flex-col overflow-hidden">
      <PageHeader
        title="Scans"
        description="Scout scan runs over finished transcripts. Open one to browse its results."
        count={hasLoaded ? total.toLocaleString() : undefined}
      >
        <form
          onSubmit={e => e.preventDefault()}
          className="relative min-w-40 max-w-sm flex-1"
        >
          <input
            type="search"
            aria-label="Search scans"
            placeholder="Search scans…"
            value={searchQuery}
            onChange={e => updateParams({ search: e.target.value, page: 1 })}
            className="input h-7 w-full"
          />
          <LoadingIndicator loading={isLoading} />
        </form>
      </PageHeader>

      <RefreshError error={error} onRetry={refetch} />
      {/* AG Grid */}
      <div className="flex-1 overflow-hidden relative">
        {/* An empty page while the next one loads is not "no results". */}
        {(!hasLoaded || (scans.length === 0 && isLoading)) && (
          <div className="absolute inset-0 z-10 bg-surface">
            <ListSkeleton
              columns={['w-48', 'w-36', 'w-36', 'w-24', 'w-40', 'flex-1']}
            />
          </div>
        )}
        {scans.length === 0 && hasLoaded && !isLoading ? (
          <div className="p-8 text-center text-sm text-muted">
            <p>
              {searchQuery
                ? `No scans match "${searchQuery}".`
                : 'No scans yet.'}
            </p>
            <p className="mt-1 text-xs">
              Scans are launched from the CLI (<code>hawk scan run</code>) and
              appear here once their results are imported.
            </p>
          </div>
        ) : (
          <div className="ag-theme-quartz rows-open relative h-full w-full">
            <ResetGridLayoutButton layout={layout} />
            <AgGridReact<ScanListItem>
              {...layout.gridProps}
              ref={gridRef}
              rowData={scans}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getRowId={getRowId}
              onRowClicked={handleRowClicked}
              onCellMouseDown={handleCellMouseDown}
              animateRows={false}
              suppressCellFocus={true}
              domLayout="normal"
            />
          </div>
        )}
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={PAGE_SIZE}
        disabled={isLoading}
        onChange={handlePageChange}
      />
    </div>
  );
}
